import { Controller } from "@hotwired/stimulus"
import html2canvas from "html2canvas";

export default class extends Controller {
  static targets = [
    "canvas",
    "signal",
    "image",
    "popup",
    "imageHeatmap",
    "imageHeatmapFile",
    "popupContent",
    "heatmapCanvas",
    "legend"
  ];

  connect() {
    this.defaultBand = this.element.dataset.defaultBand || "combined_5ghz";
    this.bandStatus = this.parseBandStatus(this.element.dataset.bandStatus);

    if (this.hasCanvasTarget && this.hasImageTarget) {
      this.abortController = new AbortController();

      this.onImageReady = () => {
        const img = this.imageTarget;
        const canvas = this.canvasTarget;

        const width = img.clientWidth || img.naturalWidth;
        const height = img.clientHeight || img.naturalHeight;

        canvas.width = width;
        canvas.height = height;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        this.width = width;
        this.height = height;
        this.cachedData = {};
        this.dataLoaded = false;

        console.log(`[Heatmap] Render size: ${width}x${height}`);
        this.loadAndRender();
      };

      if (this.imageTarget.complete) {
        this.onImageReady();
      } else {
        this.imageTarget.addEventListener("load", this.onImageReady);
      }

      window.addEventListener("resize", this.onImageReady);
    } else if (this.hasImageHeatmapFileTarget) {
      this.closeProcessPopup();
      this.autoSwitchToDefaultBand();
      this.showcHeatmapLegend();
      if (!this.hasImageOverlay(this.defaultBand)) {
        this.showProcessPopup(this.bandUnavailableMessage(this.defaultBand));
      }
    }
  }

  disconnect() {
    if (this.hasCanvasTarget && this.hasImageTarget) {
      this.abort();
      window.removeEventListener("resize", this.onImageReady);
    }
  }


  switch(event) {
    const signalKey = event.currentTarget.dataset.signal;

    if (this.hasImageHeatmapFileTarget) {
      const hasTargetImage = this.hasImageOverlay(signalKey);

      this.imageHeatmapFileTargets.forEach((el) => {
        el.classList.toggle("hidden", el.id !== signalKey);
      });
      this.activeSignal(event);

      if (hasTargetImage) {
        this.closeProcessPopup();
      } else {
        this.showProcessPopup(this.bandUnavailableMessage(signalKey));
      }
      this.showcHeatmapLegend();
    } else if (this.dataLoaded) {
      this.activeSignal(event);
      this.applyBandView(signalKey);
    }
  }

  loadAndRender() {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.dataLoaded = false;
    this.showProcessPopup("Heatmap is generating...");

    const currentPath = window.location.pathname;
    const selectedBand = this.defaultBand || "combined_5ghz";
    const url = new URL(`${currentPath}/data`, window.location.origin);
    url.searchParams.set("width", this.width);
    url.searchParams.set("height", this.height);
    url.searchParams.set("band", selectedBand);

    fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (response.ok) return response.json();
        return response.json().then(
          (err) => {
            throw new Error(err.error || "Heatmap generation failed");
          },
          (jsonError) => {
            console.error("Failed to parse error response:", jsonError);
            throw new Error("Heatmap generation failed");
          }
        );
      })
      .then((data) => {
        this.cachedData = {
          combined_2_4ghz: data.combined_2_4ghz,
          combined_5ghz: data.combined_5ghz,
          combined_6ghz: data.combined_6ghz,
        };
        this.bandStatus = data.band_status || {};
        this.dataLoaded = true;

        this.allBands().forEach((band) => {
          if (this.isBandSupported(band)) this.renderHeatmap(band);
        });

        this.autoSwitchToDefaultBand();
        this.applyBandView(this.defaultBand);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          this.dataLoaded = false;
          this.showProcessPopup(error.message);
          console.error("Fetch error:", error);
        }
      });
  }

  renderHeatmap(band) {
    const matrix = this.cachedData[band];
    const { width, height } = this;
    if (!matrix || matrix.length === 0) return false;

    const interpolated = this.interpolateMatrix(matrix, width, height);
    this.renderMatrixToCanvas(interpolated, band);
    return true;
  }

  bilinearInterpolate(matrix, x, y) {
    const rows = matrix.length;
    const cols = matrix[0].length;
    x = Math.max(0, Math.min(cols - 1, x));
    y = Math.max(0, Math.min(rows - 1, y));

    const x1 = Math.floor(x);
    const y1 = Math.floor(y);
    const x2 = Math.min(x1 + 1, cols - 1);
    const y2 = Math.min(y1 + 1, rows - 1);

    const fx = x - x1;
    const fy = y - y1;

    const v11 = matrix[y1][x1] || 0;
    const v12 = matrix[y1][x2] || 0;
    const v21 = matrix[y2][x1] || 0;
    const v22 = matrix[y2][x2] || 0;

    const v1 = v11 * (1 - fx) + v12 * fx;
    const v2 = v21 * (1 - fx) + v22 * fx;
    return v1 * (1 - fy) + v2 * fy;
  }

  interpolateMatrix(matrix, targetW, targetH) {
    const rows = matrix.length;
    const cols = matrix[0].length;
    const result = [];

    for (let y = 0; y < targetH; y++) {
      const row = [];
      for (let x = 0; x < targetW; x++) {
        const srcX = (x / targetW) * (cols - 1);
        const srcY = (y / targetH) * (rows - 1);
        row.push(this.bilinearInterpolate(matrix, srcX, srcY));
      }
      result.push(row);
    }
    return result;
  }

  renderMatrixToCanvas(matrix, band) {
    const canvas = this.canvasTarget;
    if (canvas.width !== this.width || canvas.height !== this.height) {
      canvas.width = this.width;
      canvas.height = this.height;
    }

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    const redAlpha = 153;    // 60% opacity
    const yellowAlpha = 179; // 70% opacity
    const greenAlpha = 179;  // 70% opacity
    const colorStops = {
      red: { color: [240, 89, 89], alpha: redAlpha },     // #F05959
      yellow: { color: [229, 209, 27], alpha: yellowAlpha }, // #E5D11B
      green: { color: [100, 207, 38], alpha: greenAlpha },   // #64CF26
    };

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const value = matrix[y][x];
        const i = (y * width + x) * 4;
        let r = 0, g = 0, b = 0, a = 0;

        if (value >= -90) {
          let stop = null;
          if (value >= -60) {
            stop = colorStops.green;
          } else if (value >= -75) {
            stop = colorStops.yellow;
          } else {
            stop = colorStops.red;
          }

          r = stop.color[0];
          g = stop.color[1];
          b = stop.color[2];
          a = stop.alpha;
        }

        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = a;
      }
    }

    ctx.putImageData(imageData, 0, 0);
    this.saveHeatmapImage(band);
  }

  saveHeatmapImage(band) {
    if (!this.hasHeatmapCanvasTarget) return;
    html2canvas(this.heatmapCanvasTarget, { useCORS: true, allowTaint: false }).then((canvas) => {
      canvas.toBlob((blob) => {
        const formData = new FormData();
        formData.append("heatmap", blob, `heatmap_${band}.png`);
        formData.append("band", band);

        const currentPath = window.location.pathname;
        const tokenMeta = document.querySelector("meta[name=csrf-token]");
        const csrfToken = tokenMeta ? tokenMeta.content : "";

        fetch(`${currentPath}/upload_image`, {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken },
          body: formData,
        }).catch(console.error);
      });
    });
  }

  abort() {
    if (this.abortController) this.abortController.abort();
  }

  activeSignal(event) {
    if (this.hasSignalTarget) {
      this.signalTargets.forEach((el) => el.classList.remove("active"));
      event.currentTarget.classList.add("active");
    }
  }

  applyBandView(band) {
    if (!this.hasCanvasTarget) return;

    if (!this.isBandSupported(band)) {
      this.clearCanvas();
      this.showProcessPopup(this.bandUnavailableMessage(band));
      this.showcHeatmapLegend();
      return;
    }

    if (this.renderHeatmap(band)) {
      this.closeProcessPopup();
      this.showcHeatmapLegend();
      return;
    }

    this.clearCanvas();
    this.showProcessPopup(`Heatmap data unavailable for ${this.bandLabel(band)}. Please try again.`);
    this.showcHeatmapLegend();
  }

  clearCanvas() {
    if (!this.hasCanvasTarget) return;
    const ctx = this.canvasTarget.getContext("2d", { willReadFrequently: true });
    ctx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height);
  }

  showProcessPopup(message) {
    if (this.hasPopupContentTarget) this.popupContentTarget.textContent = message;
    if (this.hasPopupTarget) this.popupTarget.classList.remove("hidden");
  }

  closeProcessPopup() {
    if (this.hasPopupTarget) this.popupTarget.classList.add("hidden");
  }

  showcHeatmapLegend() {
    if (this.hasLegendTarget) this.legendTarget.classList.remove("hidden");
  }

  allBands() {
    return ["combined_2_4ghz", "combined_5ghz", "combined_6ghz"];
  }

  isBandSupported(band) {
    const status = this.bandStatus?.[band];
    if (typeof status?.supported === "boolean") return status.supported;
    return this.hasMatrixData(band);
  }

  hasMatrixData(band) {
    const matrix = this.cachedData?.[band];
    return Array.isArray(matrix) && matrix.length > 0;
  }

  hasImageOverlay(band) {
    if (!this.hasImageHeatmapFileTarget) return false;
    return this.imageHeatmapFileTargets.some((el) => el.id === band);
  }

  bandUnavailableMessage(band) {
    const status = this.bandStatus?.[band];
    if (status?.message) return status.message;
    return `Heatmap unavailable for ${this.bandLabel(band)}.`;
  }

  bandLabel(band) {
    const labels = {
      combined_2_4ghz: "2.4 GHz",
      combined_5ghz: "5 GHz",
      combined_6ghz: "6 GHz",
    };
    return labels[band] || band;
  }

  parseBandStatus(raw) {
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_error) {
      return {};
    }
  }

  autoSwitchToDefaultBand() {
    if (!this.defaultBand || !this.hasSignalTarget) return;
    const targetSignal = this.signalTargets.find((el) => el.dataset.signal === this.defaultBand);
    if (!targetSignal) return;

    this.signalTargets.forEach((el) => el.classList.remove("active"));
    targetSignal.classList.add("active");

    if (this.hasImageHeatmapFileTarget) {
      this.imageHeatmapFileTargets.forEach((el) => {
        el.classList.toggle("hidden", el.id !== this.defaultBand);
      });
    }
  }
}
